import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal, 
  Spinner, 
  Badge, 
  Nav, 
  NavItem, 
  NavLink, 
  TabContent, 
  TabPane, 
  Table,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  InputGroup,
  Input
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { workstatusOptions, formatRoleName } from '../constants/utils';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../helpers/api';
import { translateActivity } from '../helpers/activityTranslator';
import FeatherIcon from 'feather-icons-react';
import usePermission from '../Hooks/usePermission';

const UserModal = ({
  isOpen,
  toggle,
  userData,
  loading,
  formatCreatedDate,
  onTicketClick,
  fetchUserWorkOrders
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const [activeTab, setActiveTab] = useState('details');
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Enhanced animations
  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: -50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

    const getActivity = async () => {
    try {
      setLoadingActivities(true);
      const { data } = await api.get(`user/activity?parentId=${userData?.id}`);
      setActivities(data.response || []);
    } catch (error) {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const tableRowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300
      }
    })
  };

// In your applyDateFilters function
const applyDateFilters = () => {
  if (!userData?.id) return;
  
  setIsFiltering(true);

  // Convert local dates to UTC dates at midnight
  const adjustedFromDate = fromDate ? 
    new Date(Date.UTC(
      fromDate.getFullYear(),
      fromDate.getMonth(),
      fromDate.getDate(),
      0, 0, 0
    )) : null;

  const adjustedToDate = toDate ? 
    new Date(Date.UTC(
      toDate.getFullYear(),
      toDate.getMonth(),
      toDate.getDate(),
      23, 59, 59
    )) : null;

  fetchUserWorkOrders(userData.id, adjustedFromDate, adjustedToDate)
    .finally(() => setIsFiltering(false));
};

  // Function to clear date filters
  const clearDateFilters = () => {
    setFromDate(null);
    setToDate(null);
    setSearchTerm('');
    if (!userData?.id) return;
    
    setIsFiltering(true);
    fetchUserWorkOrders(userData.id, null, null)
      .finally(() => setIsFiltering(false));
  };

    useEffect(() => {
      if (userData?.id) {
        getActivity();
      }
    }, [userData?.id]);

  // Filter work orders based on search term
  const filteredWorkOrders = userData?.reportedIssues?.filter(issue =>
    issue.ticketNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    issue.asset?.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    issue.status.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

const getStatusInfo = (status) => {

  return (
    workstatusOptions(t).find(option => option.value === status) || 
    { label: status, icon: 'help-circle', colorClass: 'bg-secondary text-white', borderColor: '#6c757d' }
  );
};


  return (
    <AnimatePresence>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          toggle={toggle}
          size="xl"
          centered
          className="modern-modal"
          backdrop="static"
          style={{ maxHeight: '90vh' }}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="modal-content border-0 shadow-lg"
            style={{ borderRadius: '12px', overflow: 'hidden' }}
          >
            {/* Enhanced Header */}
            <div 
              className="modal-header border-0 bg-gradient-primary text-white position-sticky" 
              style={{ 
                top: 0, 
                zIndex: 10,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1.5rem'
              }}
            >
              <div className="d-flex align-items-center">
                <div className="position-relative me-3">
                  {userData?.profileUrl ? (
                    <img 
                      src={userData.profileUrl} 
                      alt="Profile" 
                      className="rounded-circle border border-white border-3"
                      style={{ 
                        width: '64px', 
                        height: '64px', 
                        objectFit: 'cover',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    />
                  ) : (
                    <div className="avatar-sm bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '64px', height: '64px' }}>
                      <i className="bx bx-user text-white fs-4"></i>
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="modal-title mb-1 text-white">{userData?.name}</h4>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="text-white-50 small">
                      <i className="bx bx-envelope me-1"></i>
                      {userData?.email || t('shared.no_email_provided', 'No email provided')}
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={toggle}
                aria-label={t('shared.close', 'Close')}
              ></button>
            </div>

            {/* Enhanced Navigation */}
            <Nav 
              tabs 
              className="border-bottom-0 px-4 pt-3 bg-light"
              style={{ borderRadius: '0' }}
            >
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('details')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <i className="bx bx-user-circle me-2"></i> 
                  {t('business.components.user_modal.tab_user_details', 'User Details')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'workOrders' ? 'active' : ''}`}
                  onClick={() => setActiveTab('workOrders')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <i className="bx bx-list-ul me-2"></i> 
                  {t('shared.work_orders', 'Work Orders')}
                  {userData?.reportedIssues?.length > 0 && (
                    <Badge 
                      color="primary" 
                      pill 
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {userData.reportedIssues.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'activities' ? 'active' : ''}`}
                  onClick={() => setActiveTab('activities')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="activity" size={16} className="me-2" />
                  {t('shared.activity_log', 'Activity Log')}
                  {activities.length > 0 && (
                    <Badge 
                      color="secondary" 
                      pill 
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {activities.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            </Nav>

            {/* Modal Body */}
            <div 
              className="modal-body p-0" 
              style={{ 
                maxHeight: 'calc(90vh - 200px)',
                overflow: 'auto'
              }}
            >
              <TabContent activeTab={activeTab} className="h-100">
                {/* Details Tab */}
                <TabPane tabId="details">
                  <div className="p-4">
                    <Row className="g-4">
                      {/* Personal Information Card */}
                      <Col lg={6}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Card className="h-100 border-0 shadow-sm">
                            <CardBody className="p-4">
                              <div className="d-flex align-items-center mb-4">
                                <div className="info-icon me-3">
                                  <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                       style={{ width: '40px', height: '40px' }}>
                                    <i className="bx bx-user text-primary fs-5"></i>
                                  </div>
                                </div>
                                <div>
                                  <h5 className="mb-1 fw-bold">{t('shared.personal_information', 'Personal Information')}</h5>
                                  <p className="text-muted small mb-0">{t('business.components.user_modal.personal_info_subtitle', 'Basic user details and contact information')}</p>
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div className="info-row d-flex justify-content-between py-2 border-bottom border-light">
                                  <span className="text-muted small fw-medium">{t('shared.full_name', 'Full Name')}:</span>
                                  <span className="fw-semibold">{userData?.name || t('shared.n_a', 'N/A')}</span>
                                </div>
                                <div className="info-row d-flex justify-content-between py-2 border-bottom border-light">
                                  <span className="text-muted small fw-medium">{t('shared.username', 'Username')}:</span>
                                  <span className="fw-semibold">{userData?.username || t('shared.n_a', 'N/A')}</span>
                                </div>
                                <div className="info-row d-flex justify-content-between py-2 border-bottom border-light">
                                  <span className="text-muted small fw-medium">Email:</span>
                                  <span className="fw-semibold">{userData?.email || 'N/A'}</span>
                                </div>
                                <div className="info-row d-flex justify-content-between py-2 border-bottom border-light">
                                  <span className="text-muted small fw-medium">{t('shared.contact', 'Contact')}:</span>
                                  <span className="fw-semibold">
                                    {userData?.phoneCode && userData?.contactNumber 
                                      ? `+${userData.phoneCode} ${userData.contactNumber}`
                                      : userData?.contactNumber || t('shared.n_a', 'N/A')
                                    }
                                  </span>
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        </motion.div>
                      </Col>

                      {/* Professional Information Card */}
                      <Col lg={6}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                        >
                          <Card className="h-100 border-0 shadow-sm">
                            <CardBody className="p-4">
                              <div className="d-flex align-items-center mb-4">
                                <div className="info-icon me-3">
                                  <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                       style={{ width: '40px', height: '40px' }}>
                                    <i className="bx bx-briefcase text-success fs-5"></i>
                                  </div>
                                </div>
                                <div>
                                  <h5 className="mb-1 fw-bold">{t('shared.professional_information', 'Professional Information')}</h5>
                                  <p className="text-muted small mb-0">{t('shared.role_and_workplace_details', 'Role and workplace details')}</p>
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div className="info-row d-flex justify-content-between py-2 border-bottom border-light">
                                  <span className="text-muted small fw-medium">{t('shared.role', 'Role')}:</span>
                                  <Badge color="primary" className="fw-semibold">
                                    {formatRoleName(userData?.role, t) || t('shared.n_a', 'N/A')}
                                  </Badge>
                                </div>
                                <div className="info-row d-flex justify-content-between py-2 border-bottom border-light">
                                  <span className="text-muted small fw-medium">{t('shared.organization', 'Organization')}:</span>
                                  <span className="fw-semibold">{userData?.organization?.name || t('shared.n_a', 'N/A')}</span>
                                </div>
                                <div className="info-row d-flex justify-content-between py-2 border-bottom border-light">
                                  <span className="text-muted small fw-medium">{t('shared.verification_status', 'Verification Status')}:</span>
                                  <Badge color={userData?.verified ? 'success' : 'warning'}>
                                    <FeatherIcon
                                      icon={userData?.verified ? 'check-circle' : 'clock'}
                                      size={12}
                                      className="me-1"
                                    />
                                    {userData?.verified ? t('shared.verified', 'Verified') : t('shared.status_pending_verification', 'Pending Verification')}
                                  </Badge>
                                </div>
                                {hasPermission && hasPermission('cost:configure-rates') && (
                                  <div className="info-row d-flex justify-content-between py-2 border-bottom border-light">
                                    <span className="text-muted small fw-medium">
                                      {t('cost_tracking.user.hourly_rate', 'Hourly rate')}:
                                    </span>
                                    <span className="fw-semibold">
                                      {userData?.hourlyRate != null
                                        ? Number(userData.hourlyRate).toFixed(2)
                                        : t('shared.n_a', 'N/A')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </CardBody>
                          </Card>
                        </motion.div>
                      </Col>
                    </Row>
                  </div>
                </TabPane>

                {/* Work Orders Tab */}
                <TabPane tabId="workOrders" >
                  <div className="p-4">
                    {/* Enhanced Filter Section */}
                    <Card className="border-0 shadow-sm mb-4">
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center mb-3">
                          <i className="bx bx-filter-alt text-primary me-2 fs-5"></i>
                          <h6 className="mb-0 text-muted">{t('shared.filters_and_search', 'Filters & Search')}</h6>
                        </div>
                        
                        <Row className="g-3">
                          {/* Search Bar */}
                          <Col md={4}>
                            <InputGroup>
                              <span className="input-group-text bg-transparent border-end-0">
                                <i className="bx bx-search text-muted"></i>
                              </span>
                              <Input
                                type="text"
                                placeholder={t('shared.search_tickets_assets_status', 'Search tickets, assets, status...')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border-start-0 ps-0"
                              />
                            </InputGroup>
                          </Col>

                          {/* Date Filters */}
                        {/* From Date Picker */}
                          <Col md={3}>
                            <div className="position-relative">
                              <DatePicker
                                selected={fromDate}
                            onChange={(date) => {
                              if (date) {
                                // Convert to UTC midnight
                                const utcDate = new Date(Date.UTC(
                                  date.getFullYear(),
                                  date.getMonth(),
                                  date.getDate(),
                                  0, 0, 0
                                ));
                                setFromDate(utcDate);
                              } else {
                                setFromDate(null);
                              }
                            }}
                                selectsStart
                                startDate={fromDate}
                                endDate={toDate}
                                className="form-control"
                                placeholderText={t('shared.from_date', 'From date')}
                                popperProps={{ style: { zIndex: 100 } }}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                              />
                              <i className="bx bx-calendar position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                            </div>
                          </Col>

                          {/* To Date Picker */}
                          <Col md={3}>
                            <div className="position-relative">
                              <DatePicker
                                selected={toDate}
                                onChange={(date) => {
                              if (date) {
                                // Convert to UTC end of day
                                const utcDate = new Date(Date.UTC(
                                  date.getFullYear(),
                                  date.getMonth(),
                                  date.getDate(),
                                  23, 59, 59
                                ));
                                setToDate(utcDate);
                              } else {
                                setToDate(null);
                              }
                            }}
                                selectsEnd
                                startDate={fromDate}
                                endDate={toDate}
                                minDate={fromDate}
                                className="form-control"
                                placeholderText={t('shared.to_date', 'To date')}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                timeCaption={t('shared.time', 'Time')}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                              />
                              <i className="bx bx-calendar position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                            </div>
                          </Col>
                          <Col md={2} className="d-flex gap-2">
                            <Button 
                              color="primary" 
                              size="sm"
                              onClick={applyDateFilters}
                              disabled={isFiltering || (!fromDate && !toDate)}
                              className="flex-fill"
                            >
                              {isFiltering ? (
                                <Spinner size="sm" className="me-1" />
                              ) : (
                                <i className="bx bx-check me-1"></i>
                              )}
                              {t('shared.apply', 'Apply')}
                            </Button>
                            <Button 
                              color="outline-secondary" 
                              size="sm"
                              onClick={clearDateFilters}
                              disabled={isFiltering || (!fromDate && !toDate && !searchTerm)}
                            >
                              <i className="bx bx-x"></i>
                            </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* Work Orders Table */}
                    <div style={{ maxHeight: 'calc(90vh - 400px)', overflow: 'auto' }}>
                      {loading || isFiltering ? (
                        <div className="text-center py-5">
                          <Spinner color="primary" size="lg" />
                          <p className="text-muted mt-3">
                            {isFiltering ? t('shared.applying_filters', 'Applying filters...') : t('shared.loading_work_orders', 'Loading work orders...')}
                          </p>
                        </div>
                      ) : filteredWorkOrders.length > 0 ? (
                        <div className="table-responsive">
                          <Table hover className="mb-0 modern-table">
                            <thead className="table-light position-sticky top-0" style={{ zIndex: 0 }}>
                              <tr>
                                <th className="border-0 py-3">
                                  <i className="bx bx-hash me-1 text-muted"></i>
                                  {t('shared.ticket', 'Ticket')}
                                </th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-calendar me-1 text-muted"></i>
                                  {t('shared.date', 'Date')}
                                </th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-package me-1 text-muted"></i>
                                  {t('shared.asset', 'Asset')}
                                </th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-flag me-1 text-muted"></i>
                                  {t('shared.status', 'Status')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                             <AnimatePresence>
                                {filteredWorkOrders.map((issue, index) => {
                                    const statusInfo = getStatusInfo(issue.status); // now returns from workstatusOptions

                                    return (
                                    <motion.tr
                                        key={issue.id}
                                        custom={index}
                                        variants={tableRowVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="hidden"
                                        whileHover={{
                                        backgroundColor: '#f8f9fa',
                                        transition: { duration: 0.2 }
                                        }}
                                        className="cursor-pointer"
                                    >
                                        <td className="py-3">
                                        <div className="d-flex align-items-center">
                                            <div
                                            className="ticket-number-badge"
                                            onClick={() => onTicketClick(issue.ticketNumber)}
                                            style={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease',
                                            }}
                                            >
                                            {issue.ticketNumber}
                                            </div>
                                        </div>
                                        </td>

                                        <td className="py-3 text-muted">
                                        {formatCreatedDate(issue.createdAt)}
                                        </td>

                                        <td className="py-3">
                                        <div className="d-flex align-items-center">
                                            <div className="avatar-xs bg-light rounded me-2 d-flex align-items-center justify-content-center">
                                            <i className="bx bx-package text-primary"></i>
                                            </div>
                                            <span className="fw-medium">
                                            {issue.asset?.assetName || 'N/A'}
                                            </span>
                                        </div>
                                        </td>

                                        <td className="py-3">
                                        <Badge
                                            className={`d-inline-flex align-items-center ${statusInfo.colorClass}`}
                                            style={{
                                            padding: '6px 12px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: '500',
                                            border: `1px solid ${statusInfo.borderColor}`
                                            }}
                                        >
                                            <FeatherIcon icon={statusInfo.icon} size={12} className="me-1" />
                                            {statusInfo.label}
                                        </Badge>
                                        </td>
                                    </motion.tr>
                                    );
                                })}
                                </AnimatePresence>
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                            <i className="bx bx-search text-muted fs-1"></i>
                          </div>
                          <h5 className="text-muted">{t('shared.no_work_orders_found', 'No work orders found')}</h5>
                          <p className="text-muted mb-0">
                            {searchTerm || fromDate || toDate 
                              ? t('shared.try_adjusting_filters', 'Try adjusting your filters or search terms')
                              : t('business.components.user_modal.no_reported_work_orders', 'This user has no reported work orders')
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabPane>

                   <TabPane tabId="activities">
                      <div className="p-4">
                        <Card className="border-0 shadow-sm">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="info-icon me-3">
                                <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                     style={{ width: '40px', height: '40px' }}>
                                  <FeatherIcon icon="activity" size={20} className="text-info" />
                                </div>
                              </div>
                              <div>
                                <h5 className="mb-1 fw-bold">{t('shared.activity_timeline', 'Activity Timeline')}</h5>
                                <p className="text-muted small mb-0">{t('business.components.user_modal.activity_timeline_subtitle', 'Complete history of user activities')}</p>
                              </div>
                            </div>

                            {loadingActivities ? (
                              <div className="text-center py-5">
                                <Spinner color="primary" size="lg" />
                                <p className="text-muted mt-3">{t('shared.loading_activity_log', 'Loading activity log...')}</p>
                              </div>
                            ) : activities.length > 0 ? (
                              <div className="activity-timeline" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {activities.map((activity, index) => (
                                  <motion.div 
                                    key={activity.id} 
                                    className="timeline-item d-flex mb-4"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                  >
                                    <div className="timeline-marker me-3">
                                      <div className="timeline-dot bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                           style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                                        <FeatherIcon icon="clock" size={14} className="text-white" />
                                      </div>
                                      {index < activities.length - 1 && (
                                        <div className="timeline-line bg-light" 
                                             style={{ width: '2px', height: '40px', marginLeft: '15px', marginTop: '4px' }}></div>
                                      )}
                                    </div>
                                    <div className="timeline-content flex-grow-1">
                                      <div className="timeline-card bg-light rounded p-3">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                          <div>
                                            <div className="fw-semibold text-dark">{activity.userName}</div>
                                            <div className="text-muted">{translateActivity(activity, t)}</div>
                                          </div>
                                          <Badge color="light" className="text-muted small">
                                            <FeatherIcon icon="calendar" size={12} className="me-1" />
                                            {formatCreatedDate(activity?.createdAt)}
                                          </Badge>
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-5">
                                <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                     style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                                  <FeatherIcon icon="clock" size={24} className="text-muted" />
                                </div>
                                <h5 className="text-muted">{t('shared.no_activity_recorded', 'No activity recorded')}</h5>
                                <p className="text-muted mb-0">{t('shared.activity_will_appear_here', 'Activity will appear here as the ticket progresses')}</p>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </div>
                    </TabPane>
              </TabContent>
            </div>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default UserModal;